<template>
  <div class="base-list" :style="styles">
    <div v-if="title" class="base-list--title">
      <h2>{{ title }}</h2>
    </div>
    <div v-if="componentHasHeader" class="base-list--header">
      <span v-if="draggable && dragSideLeft"></span>
      <h3 v-for="component in layout" :key="component.id">{{ component.header }}</h3>
      <span v-if="draggable && dragSideRight"></span>
    </div>
    <Container
      class="base-list--list"
      drag-handle-selector=".base-list--drag"
      lock-axis="y"
      @drop="onDrop"
    >
      <Draggable v-for="(item, inx) in values" :key="inx" :drag-not-allowed="!draggable" class="base-list--draggable">
        <div class="base-list--item">
          <span v-if="draggable && dragSideLeft" class="base-list--drag"><i class="fal fa-grip-lines"></i></span>
          <span v-if="autoIncrement" class="base-list--increment">{{ inx + 1 }}</span>
          <component
            v-for="component in layout"
            :key="component.id"
            :is="component.component"
            v-bind="getProps(component)"
            :value="item[component.id]"
            @update="updateValue(component, inx, $event)"
          >{{ component.slot }}</component>
          <span v-if="draggable && dragSideRight" class="base-list--drag"><i class="fal fa-grip-lines"></i></span>
        </div>
      </Draggable>
    </Container>
    <div
      v-if="hasError"
      class="input-hint"
    >
      <div
        v-if="hasError"
        class="input-hint--error"
        v-html="errorMessage"
      ></div>
    </div>
  </div>
</template>

<script>
import { Container, Draggable } from 'vue-dndrop';

export default {
  name: 'BaseList',
  components: {
    Container,
    Draggable,
  },
  model: {
    prop: 'value',
    event: 'update',
  },
  props: {
    value: {
      type: Array,
      default: () => ([]),
    },
    layout: {
      type: Array,
      default: () => ([]),
    },
    title: {
      type: String,
      default: '',
    },
    autoIncrement: {
      type: [Boolean, String],
      default: false,
    },
    draggable: {
      type: Boolean,
      default: false,
    },
    dragSide: {
      type: String,
      default: 'right',
      validator: (value) => ['left', 'right'].includes(value),
    },
    error: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      list: this.value,
      internalError: this.error,
    };
  },
  mounted() {
    const allNullable = this.layout.every((field) => field.nullable);

    if (allNullable) {
      console.warn(`<${this.$options.name}> All fields are nullable. This would result in an infinite loop.`);
      return;
    }

    this.values = this.convertValue(this.values);
    if (this.values.length === 0 || this.noEntryEmpty(this.values)) {
      this.addItem();
    }
  },
  computed: {
    componentHasHeader() {
      return this.layout.some((component) => component.header);
    },
    hasError() {
      return this.errorMessage !== '';
    },
    errorMessage: {
      get() {
        return this.internalError || this.error;
      },
      set(value) {
        this.internalError = value;
        this.$emit('error', value);
      },
    },
    values: {
      get() {
        return this.list;
      },
      set(internalValue) {
        const value = this.convertValue(internalValue);
        const existingEntryEmpty = value.slice(0, value.length - 1)
          .some((item) => Object.entries(item)
            .some(([key, val]) => !this.fieldHasValue(key, val)));
        const lastEntryEmpty = value.length > 0 && Object.entries(value[value.length - 1])
          .every(([key, val]) => !this.fieldHasValue(key, val));
        const uniqueLayoutComponents = this.layout.filter((component) => component.unique);
        const hasArrayDuplicates = (arr = []) => new Set(arr).size !== arr.length;
        const setValue = (val) => {
          let list = val;
          let removedEmpty = [];

          // Remove empty entries and auto increment the list
          if (this.autoIncrement) {
            const componentHasNoId = !this.layout.filter((component) => component.id).length;
            const incrementValue = typeof this.autoIncrement === 'string' ? this.autoIncrement : 'id';

            removedEmpty = list.filter((item) => Object.entries(item)
              .filter(([name]) => name !== incrementValue)
              .some(([, v]) => v !== ''));

            if (componentHasNoId) {
              removedEmpty = removedEmpty.map((item) => item.undefined).filter((item) => item !== '');
            } else {
              removedEmpty = removedEmpty.map((item, inx) => ({
                ...item,
                [incrementValue]: inx + 1,
              }));

              const emptyItem = this.layout.reduce((acc, item) => ({ ...acc, [item.id]: '' }), {});
              list = [...removedEmpty, emptyItem];
            }
          } else {
            removedEmpty = list.filter((item) => Object.values(item).some((v) => v !== ''));
          }

          this.list = list;
          this.$emit('update', removedEmpty);
        };

        // Prevent adding duplicate values
        if (uniqueLayoutComponents.length) {
          const hasDuplicates = uniqueLayoutComponents.some((component) => {
            const items = value.map((item) => item[component.id]).filter((item) => item !== '' && item !== null && item !== undefined);

            return hasArrayDuplicates(items);
          });

          if (hasDuplicates) {
            this.errorMessage = 'Duplicate entries are not allowed.';
            setValue(value);
          } else {
            this.errorMessage = '';
          }
        }

        if (this.noEntryEmpty(value)) {
          this.addItem(value);
        } else if (existingEntryEmpty && lastEntryEmpty) {
          this.removeItem(value, value.length - 1);
        } else if (existingEntryEmpty) {
          if (existingEntryEmpty) {
            const val = value.filter((item) => Object.values(item).some((v) => v !== '' && v !== null));

            if (this.noEntryEmpty(val)) {
              this.addItem(val);
            } else {
              setValue(val);
            }
          }
        } else {
          setValue(value);
        }
      },
    },
    dragSideLeft() {
      return this.dragSide === 'left';
    },
    dragSideRight() {
      return this.dragSide === 'right';
    },
    styles() {
      const columns = this.layout.map((component) => component.width || '1fr').join(' ');
      const increment = this.autoIncrement ? '1rem ' : '';
      const dragSideColumns = this.dragSideLeft
        ? `2rem ${increment}${columns}`
        : `${increment}${columns} 2rem`;

      return {
        '--grid-template-columns': this.draggable ? dragSideColumns : `${increment}${columns}`,
      };
    },
  },
  methods: {
    convertValue(value) {
      const arrayContainsObjects = value.some((item) => typeof item === 'object');

      if (!arrayContainsObjects) {
        return value.map((item) => ({ undefined: item }));
      }
      return value;
    },
    fieldHasValue(key, val) {
      // Check if the field has a value, nullable fields are always valid
      const nullable = this.layout.find((comp) => comp.id === key)?.nullable;

      if (nullable !== undefined && nullable) {
        return true;
      }
      return val !== '' && val !== null;
    },
    noEntryEmpty(value) {
      return value.every((item) => Object.entries((item)).every(([key, val]) => this.fieldHasValue(key, val)));
    },
    updateValue(component, inx, val) {
      this.values = this.values.map((v, i) => i === inx ? { ...v, [component.id]: val } : v)
    },
    addItem(value = null) {
      const emptyItem = this.layout.reduce((acc, item) => ({ ...acc, [item.id]: '' }), {});
      const items = value ? [...value, emptyItem] : [...this.values, emptyItem];

      this.values = items;
    },
    removeItem(value = null, index = null) {
      const values = value || value.length ? value : this.values;
      const indexToRemove = index !== null ? index : values.length - 1;
      const items = values.filter((item, inx) => inx !== indexToRemove);

      this.values = items;
    },
    getProps(component) {
      if (component.unique) {
        // Conditional items for BaseSelect
        if (component.component === 'BaseSelect') {
          return {
            ...component.props,
            items: component.props.items.map((item) => ({
              ...item,
              disabled: this.values.some((v) => v[component.id] === item.value),
            })),
          };
        }
      }
      return component.props;
    },
    applyDrag: (arr, dragResult) => {
      const { removedIndex, addedIndex, payload } = dragResult;
      if (removedIndex === null && addedIndex === null) return arr;

      const result = [...arr];
      let itemToAdd = payload;

      if (removedIndex !== null) {
        itemToAdd = result.splice(removedIndex, 1)[0];
      }

      if (addedIndex !== null) {
        result.splice(addedIndex, 0, itemToAdd);
      }

      return result;
    },
    onDrop(dropResult) {
      this.values = this.applyDrag(this.values, dropResult);
      this.$emit('update', this.applyDrag(this.value, dropResult));
    },
  },
};
</script>

<style lang="scss" scoped>
.base-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &--list {
    display: flex;
    flex-direction: column;
  }

  &--draggable {
    padding: .5rem 0;
    overflow: visible !important;
  }

  &--title {
    font-size: 1.25rem;
  }

  &--header {
    font-weight: 600;
  }

  &--item,
  &--header {
    display: grid;
    grid-template-columns: var(--grid-template-columns);
    gap: 1rem;
  }

  &--increment {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 1.5rem;
    font-weight: 300;
  }

  &--drag {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: move;
  }
}

.input-hint {
  display: flex;
  justify-content: flex-end;
  grid-column-gap: 1em;
  padding: 0.25em 0;

  &--error {
    flex: 1;
    display: block;
    font-size: 12px;
    color: var(--base-input--error-color);
    line-height: 1.25;
  }
}
</style>
